import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, X, Upload, Loader2, ImagePlus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const buildAbs = (u) => {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  if (u.startsWith('/api/')) return `${BACKEND_URL}${u}`;
  return u;
};

/**
 * Variants editor — each variant supports:
 *   - label (e.g. "15 cm")             [required]
 *   - price_delta (added to base)      [optional, default 0]
 *   - image_url (per-variant image)    [optional, falls back to main]
 *   - cj_vid (per-variant CJ VID)      [optional, for order routing]
 *
 *   value:    [{label, price_delta, image_url?, cj_vid?, stock?}]
 *   onChange: (newArray) => void
 */
export const VariantsEditor = ({ value = [], onChange }) => {
  const [uploadingIdx, setUploadingIdx] = useState(-1);

  const add = () => onChange([...value, { label: '', price_delta: 0, image_url: '', cj_vid: '' }]);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const update = (i, patch) => onChange(value.map((v, idx) => idx === i ? { ...v, ...patch } : v));

  const handleVariantImage = async (i, file) => {
    if (!file) return;
    setUploadingIdx(i);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(`${API}/admin/upload-image`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      update(i, { image_url: res.data.url });
      toast.success('Variant image uploaded');
    } catch (err) {
      toast.error(`Upload failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setUploadingIdx(-1);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          Sizes / variants. Each can have its own image &amp; CJ VID for routing.
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={add}
          className="text-xs h-7"
          data-testid="add-variant-button"
        >
          <Plus className="w-3 h-3 mr-1" /> Add size
        </Button>
      </div>

      {value.length > 0 && (
        <div className="space-y-2">
          {value.map((v, i) => (
            <div key={i} className="bg-muted/40 rounded-md p-2 space-y-2" data-testid={`variant-row-${i}`}>
              <div className="flex items-center gap-2">
                {/* Variant image thumb / uploader */}
                <label className="relative w-12 h-12 rounded-md overflow-hidden border border-border bg-card flex-shrink-0 cursor-pointer hover:border-accent transition group">
                  {v.image_url ? (
                    <>
                      <img src={buildAbs(v.image_url)} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); update(i, { image_url: '' }); }}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5 opacity-0 group-hover:opacity-100 transition"
                        title="Remove image"
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      {uploadingIdx === i ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleVariantImage(i, e.target.files?.[0])}
                    className="hidden"
                    disabled={uploadingIdx === i}
                    data-testid={`variant-image-input-${i}`}
                  />
                </label>

                <div className="flex-1">
                  <Input
                    placeholder="Label (e.g. 15 cm)"
                    value={v.label || ''}
                    onChange={(e) => update(i, { label: e.target.value })}
                    className="h-8 text-sm"
                    data-testid={`variant-label-${i}`}
                  />
                </div>
                <div className="w-20">
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Δ price"
                    value={v.price_delta === undefined ? '' : v.price_delta}
                    onChange={(e) => update(i, { price_delta: parseFloat(e.target.value) || 0 })}
                    className="h-8 text-sm"
                    data-testid={`variant-delta-${i}`}
                  />
                </div>
                <div className="w-16">
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    placeholder="Stock"
                    title="Stock for this variant"
                    value={v.stock === undefined || v.stock === null ? '' : v.stock}
                    onChange={(e) => update(i, { stock: e.target.value === '' ? null : Math.max(0, parseInt(e.target.value, 10) || 0) })}
                    className="h-8 text-sm"
                    data-testid={`variant-stock-${i}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="text-muted-foreground hover:text-red-500 transition p-1"
                  title="Remove variant"
                  data-testid={`variant-remove-${i}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* CJ VID row — only shown if a label has been entered */}
              {v.label && (
                <div className="flex items-center gap-2 pl-14">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground w-12">CJ VID</span>
                  <Input
                    placeholder="Optional CJ variant id (vid) for order routing"
                    value={v.cj_vid || ''}
                    onChange={(e) => update(i, { cj_vid: e.target.value })}
                    className="h-7 text-xs flex-1"
                    data-testid={`variant-cjvid-${i}`}
                  />
                </div>
              )}
            </div>
          ))}
          <p className="text-[11px] text-muted-foreground">
            Δ price adds to base price. Variant image overrides main image when selected. Variant CJ VID routes the order to the right SKU at CJ.
          </p>
        </div>
      )}
    </div>
  );
};

export default VariantsEditor;
